import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { SectionHeader } from "./Top3";
import { ExternalLink, RefreshCw, ArrowUpToLine, Search, X } from "lucide-react";
import EditableText from "./EditableText";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
} from "./ui/dropdown-menu";
import { toast } from "sonner";

const useStickyState = (key, initial) => {
  const [v, setV] = useState(() => {
    if (typeof window === "undefined") return initial;
    return localStorage.getItem(`op:${key}`) || initial;
  });
  useEffect(() => {
    try { localStorage.setItem(`op:${key}`, v); } catch {}
  }, [key, v]);
  return [v, setV];
};

const priorityLabel = (p) => (p === 4 ? "P1" : p === 3 ? "P2" : p === 2 ? "P3" : "P4");
const priorityColor = (p) =>
  p === 4 ? "text-[#F5A623]" : p === 3 ? "text-[#00E5FF]" : "text-[#A0A5B5]";

const BUSINESS_FROM_TASK = (t, projectMap) => {
  // projectMap: { project_id: business_name }
  return projectMap[t.project_id] || "OPERATOR OS";
};

const dueTimeFromTask = (t) => {
  const s = t.due?.string?.toUpperCase();
  if (!s) return null;
  if (["TODAY", "TOMORROW", "THIS WEEK", "NEXT WEEK"].includes(s)) return s;
  return s;
};

const PromoteMenu = ({ task, top3, projectMap, onPromoted }) => {
  const [busy, setBusy] = useState(false);
  const [top3Cache, setTop3Cache] = useState(top3);

  const openMenu = async () => {
    // Refresh top3 to show current occupants
    try {
      const list = await api.top3();
      setTop3Cache(list);
    } catch {
      /* keep cache */
    }
  };

  const promote = async (slot) => {
    setBusy(true);
    try {
      await api.promoteToTop3({
        slot,
        content: task.content,
        business: BUSINESS_FROM_TASK(task, projectMap),
        todoist_task_id: task.id,
        todoist_url: task.url,
        due_time: dueTimeFromTask(task),
      });
      toast("Promoted to Top 3", { description: `Slot 0${slot} · ${task.content}` });
      onPromoted?.();
    } catch (e) {
      toast("Promotion failed", { description: e?.response?.data?.detail || "" });
    } finally {
      setBusy(false);
    }
  };

  return (
    <DropdownMenu onOpenChange={(o) => o && openMenu()}>
      <DropdownMenuTrigger
        disabled={busy}
        data-testid={`promote-${task.id}`}
        className="text-[#5E657D] hover:text-[#00E5FF] transition-colors disabled:opacity-40"
        title="Promote to Today's Top 3"
      >
        <ArrowUpToLine size={13} />
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="end"
        className="bg-[#0A0C14] border-white/10 min-w-[280px]"
      >
        <DropdownMenuLabel className="op-tag !text-[10px] !font-normal">
          PROMOTE TO TOP 3 · REPLACE
        </DropdownMenuLabel>
        <DropdownMenuSeparator className="bg-white/[0.05]" />
        {[1, 2, 3].map((slot) => {
          const current = (top3Cache || []).find((x) => x.slot === slot);
          return (
            <DropdownMenuItem
              key={slot}
              onSelect={() => promote(slot)}
              data-testid={`promote-${task.id}-slot-${slot}`}
              className="flex items-start gap-3 py-2 focus:bg-white/5 focus:text-white cursor-pointer"
            >
              <span className="op-mono text-[10px] tracking-[0.24em] text-[#5E657D] mt-0.5">
                0{slot}
              </span>
              <div className="flex-1 min-w-0">
                <div className="op-tag !text-[9px] mb-0.5">
                  {current?.business || "EMPTY"}
                </div>
                <div className="text-white text-xs leading-snug truncate">
                  {current?.objective || "— nothing here —"}
                </div>
              </div>
            </DropdownMenuItem>
          );
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

const CHIPS = [
  { key: "fire", label: "ALL FIRE", filter: "(overdue | p1) & !@waiting" },
  { key: "overdue", label: "OVERDUE", filter: "overdue" },
  { key: "p1", label: "P1", filter: "p1 & !@waiting" },
  { key: "next7", label: "NEXT 7 DAYS", filter: "7 days & !@waiting" },
];

const PROJECT_CHIPS = [
  { key: "active", label: "ACTIVE" },
  { key: "today", label: "TODAY" },
  { key: "overdue", label: "OVERDUE" },
];

const todayISO = () => new Date().toISOString().slice(0, 10);

const isToday = (t) => {
  const today = todayISO();
  return t.due?.date?.slice(0, 10) === today;
};

const isOverdue = (t) => {
  const today = todayISO();
  return t.due?.date && t.due.date.slice(0, 10) < today;
};

const projectFilter = (chip, tasks) => {
  switch (chip) {
    case "today":
      return tasks.filter(isToday);
    case "overdue":
      return tasks.filter(isOverdue);
    default:
      return tasks;
  }
};

// Sort: priority desc (P1=4 first), then due date asc (nulls last)
const sortTasks = (tasks) =>
  [...tasks].sort((a, b) => {
    const pa = a.priority || 1;
    const pb = b.priority || 1;
    if (pa !== pb) return pb - pa;
    const da = a.due?.date || "9999-12-31";
    const db_ = b.due?.date || "9999-12-31";
    return da.localeCompare(db_);
  });

const TodoistTaskList = ({
  mode = "filter",
  filter: initialFilter = "today",
  projectId = null,
  code = "TD",
  label = "TODOIST",
  caption = "",
  testid = "todoist-panel",
  emptyText = "Nothing to show here.",
  notConnectedText = "Connect Todoist in Settings to see live tasks.",
  noProjectText = "No Todoist project mapped to this business yet. Add it in Settings → Project Routing.",
  showChips = false,
  showProjectChips = false,
  showPromote = false,
  showSearch = false,
}) => {
  const [state, setState] = useState({ status: "loading", tasks: [] });
  const [refreshing, setRefreshing] = useState(false);
  const [activeChip, setActiveChip] = useState(showChips ? "fire" : null);
  const [activeProjectChip, setActiveProjectChip] = useStickyState(`chip:${testid}`, "active");
  const [query, setQuery] = useState("");
  const activeFilter = activeChip
    ? CHIPS.find((c) => c.key === activeChip)?.filter
    : initialFilter;

  const [top3Cache, setTop3Cache] = useState([]);
  const [projectMap, setProjectMap] = useState({}); // project_id -> business

  const load = async () => {
    try {
      const { connected } = await api.todoistStatus();
      if (!connected) {
        setState({ status: "disconnected", tasks: [] });
        return;
      }
      if (mode === "project") {
        if (!projectId) {
          setState({ status: "no_project", tasks: [] });
          return;
        }
        const tasks = await api.todoistTasksByProject(projectId);
        setState({ status: "ok", tasks });
      } else {
        const tasks = await api.todoistTasks(activeFilter);
        setState({ status: "ok", tasks });
      }

      if (showPromote) {
        try {
          const [t3, routing] = await Promise.all([api.top3(), api.todoistRouting()]);
          setTop3Cache(t3);
          const reverse = {};
          Object.entries(routing.business_map || {}).forEach(([biz, pid]) => {
            if (pid) reverse[pid] = biz;
          });
          setProjectMap(reverse);
        } catch { /* non-blocking */ }
      }
    } catch (e) {
      setState({ status: "error", tasks: [], error: e?.response?.data?.detail || "Todoist error" });
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [mode, activeFilter, projectId]);

  // Also refresh when a Todoist reconciliation event fires from the sidebar
  useEffect(() => {
    const onSync = () => load();
    window.addEventListener("operator:sync", onSync);
    return () => window.removeEventListener("operator:sync", onSync);
    // eslint-disable-next-line
  }, [mode, activeFilter, projectId]);

  const refresh = async () => {
    setRefreshing(true);
    await load();
    setRefreshing(false);
  };

  return (
    <section data-testid={testid}>
      <SectionHeader code={code} label={label} caption={caption} />
      <div className="op-panel p-6">
        {/* Search (business tabs) */}
        {showSearch && state.status === "ok" && (
          <div className="relative mb-5">
            <Search
              size={12}
              className="absolute left-3 top-1/2 -translate-y-1/2 text-[#5E657D]"
            />
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="FILTER TASKS BY CONTENT…"
              className="op-input op-mono text-xs !py-2 !pl-9 !pr-9 tracking-[0.14em] uppercase placeholder:tracking-[0.24em] placeholder:text-[#5E657D]"
              data-testid={`${testid}-search`}
            />
            {query && (
              <button
                onClick={() => setQuery("")}
                data-testid={`${testid}-search-clear`}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-[#5E657D] hover:text-[#00E5FF]"
                aria-label="Clear search"
              >
                <X size={12} />
              </button>
            )}
          </div>
        )}

        {/* Filter chips (War Room Flagged) */}
        {showChips && state.status !== "disconnected" && (
          <div className="flex flex-wrap gap-2 mb-5">
            {CHIPS.map((c) => (
              <button
                key={c.key}
                onClick={() => setActiveChip(c.key)}
                data-testid={`${testid}-chip-${c.key}`}
                className={`op-mono text-[10px] tracking-[0.24em] px-3 py-1.5 border transition-colors duration-200 ${
                  activeChip === c.key
                    ? "border-[#00E5FF] text-[#00E5FF] bg-[rgba(0,229,255,0.06)]"
                    : "border-white/10 text-[#A0A5B5] hover:text-white hover:border-white/25"
                }`}
              >
                {c.label}
              </button>
            ))}
          </div>
        )}

        {/* Project scope chips (Business tabs) — with per-chip counts */}
        {showProjectChips && state.status === "ok" && (
          <div className="flex flex-wrap gap-2 mb-5">
            {PROJECT_CHIPS.map((c) => {
              const n =
                c.key === "active"
                  ? state.tasks.length
                  : c.key === "today"
                  ? state.tasks.filter(isToday).length
                  : state.tasks.filter(isOverdue).length;
              return (
                <button
                  key={c.key}
                  onClick={() => setActiveProjectChip(c.key)}
                  data-testid={`${testid}-pchip-${c.key}`}
                  className={`op-mono text-[10px] tracking-[0.24em] px-3 py-1.5 border transition-colors duration-200 inline-flex items-center gap-2 ${
                    activeProjectChip === c.key
                      ? "border-[#00E5FF] text-[#00E5FF] bg-[rgba(0,229,255,0.06)]"
                      : "border-white/10 text-[#A0A5B5] hover:text-white hover:border-white/25"
                  }`}
                >
                  <span>{c.label}</span>
                  <span
                    className={`op-mono text-[9px] tracking-[0.15em] ${
                      c.key === "overdue" && n > 0 && activeProjectChip !== c.key
                        ? "text-[#F5A623]"
                        : "opacity-70"
                    }`}
                  >
                    · {n}
                  </span>
                </button>
              );
            })}
          </div>
        )}

        <div className="flex items-center justify-between mb-4">
          <span className="op-tag">
            {state.status === "ok"
              ? `${(showProjectChips ? projectFilter(activeProjectChip, state.tasks) : state.tasks).length} ${
                  showProjectChips ? activeProjectChip.toUpperCase() : "ACTIVE"
                }`
              : ""}
          </span>
          {state.status === "ok" && (
            <button
              onClick={refresh}
              disabled={refreshing}
              data-testid={`${testid}-refresh`}
              className="op-tag hover:text-[#00E5FF] inline-flex items-center gap-1.5"
            >
              <RefreshCw size={10} className={refreshing ? "animate-spin" : ""} />
              REFRESH
            </button>
          )}
        </div>

        {state.status === "loading" && <div className="op-microcopy op-flicker">SYNC · TODOIST</div>}
        {state.status === "disconnected" && (
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div className="text-[#A0A5B5] text-sm">{notConnectedText}</div>
            <a href="/settings" className="op-btn inline-flex items-center gap-2" data-testid={`${testid}-connect-cta`}>
              CONNECT TODOIST <ExternalLink size={12} />
            </a>
          </div>
        )}
        {state.status === "no_project" && (
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div className="text-[#A0A5B5] text-sm">{noProjectText}</div>
            <a href="/settings" className="op-btn inline-flex items-center gap-2" data-testid={`${testid}-map-cta`}>
              MAP PROJECT <ExternalLink size={12} />
            </a>
          </div>
        )}
        {state.status === "error" && (
          <div className="text-[#F5A623] op-mono text-xs" data-testid={`${testid}-error`}>
            {String(state.error)}
          </div>
        )}
        {state.status === "ok" && (() => {
          const scoped = showProjectChips ? projectFilter(activeProjectChip, state.tasks) : state.tasks;
          const q = query.trim().toLowerCase();
          const searched = q ? scoped.filter((t) => (t.content || "").toLowerCase().includes(q)) : scoped;
          const visible = sortTasks(searched);
          if (visible.length === 0) {
            return (
              <div className="text-[#5E657D] text-sm" data-testid={`${testid}-empty`}>
                {q
                  ? `No tasks match "${query}".`
                  : showProjectChips && activeProjectChip !== "active"
                  ? `Nothing ${activeProjectChip.toUpperCase()} in this project.`
                  : emptyText}
              </div>
            );
          }
          return (
            <ul className="divide-y divide-white/[0.04]" data-testid={`${testid}-list`}>
              {visible.map((t) => (
                <li
                  key={t.id}
                  data-testid={`todoist-row-${t.id}`}
                  className={`py-3 pl-3 flex items-start gap-4 group border-l-2 ${
                    t.priority === 4 ? "border-l-[#F5A623]" : "border-l-transparent"
                  }`}
                >
                  <button
                    onClick={async () => {
                      // Optimistic removal
                      setState((s) => ({ ...s, tasks: s.tasks.filter((x) => x.id !== t.id) }));
                      try {
                        await api.closeTodoistTask(t.id);
                        toast("Closed in Todoist", { description: t.content.slice(0, 60) });
                      } catch (e) {
                        toast("Close failed", { description: e?.response?.data?.detail || "" });
                        refresh();
                      }
                    }}
                    data-testid={`todoist-close-${t.id}`}
                    className="op-check mt-0.5"
                    aria-label="Complete in Todoist"
                    title="Complete in Todoist"
                  />
                  <span className={`op-mono text-[10px] tracking-[0.24em] mt-1 w-8 shrink-0 ${priorityColor(t.priority)}`}>
                    {priorityLabel(t.priority)}
                  </span>
                  <EditableText
                    value={t.content}
                    onSave={async (newVal) => {
                      try {
                        await api.updateTodoistTask(t.id, newVal);
                        // Reflect immediately in local state
                        setState((s) => ({
                          ...s,
                          tasks: s.tasks.map((x) => x.id === t.id ? { ...x, content: newVal } : x),
                        }));
                        toast("Updated in Todoist");
                      } catch (e) {
                        toast("Todoist update failed", { description: e?.response?.data?.detail || "" });
                        throw e;
                      }
                    }}
                    className="text-white text-sm flex-1"
                    multiline
                    testid={`todoist-content-${t.id}`}
                    saveNotice={null}
                  />
                  {t.due?.string && (
                    <span className="op-tag hidden sm:inline">{t.due.string.toUpperCase()}</span>
                  )}
                  {showPromote && (
                    <PromoteMenu
                      task={t}
                      top3={top3Cache}
                      projectMap={projectMap}
                      onPromoted={refresh}
                    />
                  )}
                  {t.url && (
                    <a
                      href={t.url}
                      target="_blank"
                      rel="noreferrer"
                      className="text-[#5E657D] hover:text-[#00E5FF]"
                    >
                      <ExternalLink size={12} />
                    </a>
                  )}
                </li>
              ))}
            </ul>
          );
        })()}
      </div>
    </section>
  );
};

export default TodoistTaskList;
